import { Box, makeStyles } from "@material-ui/core";
import { AddBoxOutlined, CloudDownload, Publish } from "@material-ui/icons";
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import TopBar from "../../components/Common/TopBar";
import ParcelInvestigation from "../../components/IncidentManagement";
import EditIncidentFormDialog from "../../components/IncidentManagement/EditIncidentForm/dialog";
import { getAllUsers, getBranchList } from "../../actions/commonAction";
import UniDialog from "../../components/Common/UniDialog";
import UploadIncidentForm from "../../components/IncidentManagement/UploadIncidentForm";
import { batchUploadIncidents, downloadIncidentExcel, getAllDropDownFields, getIncidentCount } from "../../actions/incidentManagementActions";
import FullscreenLoading from "../../components/Common/FullscreenLoading";
import { useAlert } from "../../utilities/hooks";
import { alertType } from "../../components/Common/UniAlert/context";
import { getOpenIncidentFormDialog, handleOpenIncidentFormDialog } from "../../reducers/uiSlice";
import { getUserPreference } from "../../utilities/common";
import IncidentMultiSearch from "../../components/IncidentManagement/IncidentMultiSearch/index";
import IncidentKeyAccount from "../../components/IncidentManagement/IncidentKeyAccount";
import IncidentCompensated from "../../components/IncidentManagement/IncidentCompensated";

import _ from "lodash";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
    },
    appBarClasses: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: theme.spacing(1)
    },
    headerContainer: {
        margin: theme.spacing(1),
    },
    topBarButton: {
        marginLeft: theme.spacing(2),
        color: 'unset',
        '&:hover': {
            color: theme.palette.text.primary
        }
    }
}))

function IncidentManagement() {
    const { t } = useTranslation('incidentManagement');
    const dispatch = useDispatch();
    const alertDialog = useAlert();
    const classes = useStyles();
    const userPref = getUserPreference();

    const networkLoading = useSelector(state => state.incidentManagement.loading);
    const openIncidentForm = useSelector(getOpenIncidentFormDialog);

    const [uploadExcelDialog, setUploadExcelDialog] = useState(false);
    const [fullScreenLoading, setFullScreenLoading] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStarted, setUploadStarted] = useState(false);
    const [isFormUpdated, setIsFormUpdated] = useState(false);
    const [filters, setFilters] = useState({});
    const investigationList = useSelector(state => state.incidentManagement.investigationList);


    const [manageIndex, setManageIndex] = useState('incident');

    const manageIndices = {
        incident: {
            id: 0,
            text: t('incident')
        },
        // multiSearch: {
        //     id: 1,
        //     text: t('multi_search')
        // },
        keyAccount: {
            id: 2,
            text: t('key_account')
        },
        compensatedOrder: {
            id: 3,
            text: t('compensated_order')
        }
    };

    const handleManageIndex = (event) => {
        setManageIndex(event.target.value);
    }
    useEffect(() => {
        initialCalls()
    }, [])

    const initialCalls = () => {
        setFullScreenLoading(true);
        Promise.all([
            dispatch(getBranchList()),
            dispatch(getAllDropDownFields()),
            dispatch(getAllUsers()),
            dispatch(getIncidentCount()),
        ]).catch(error => {
            //This page needs these values to work properly, so we ask users to retry
            alertDialog.addAlertDialog(
                t('incident_management'),
                `${t('dialog:initialize_failed')} ${t('common:retry')}`,
                alertType.ERROR,
                t('common:retry'),
                () => {
                    initialCalls();
                    alertDialog.hideAlert();
                }, 'primary', false, false);
            alertDialog.showAlert();
        }).finally(() => {
            setFullScreenLoading(false);
        })
    }

    const onFileSubmit = (file) => {
        let progressOffset = 10;
        setUploadStarted(true);
        batchUploadIncidents(userPref.userId, file, (event) => {
            let percent = Math.round((100 * event.loaded) / event.total) - progressOffset;
            setUploadProgress(percent);
        }).then(response => {
            let responseData = response?.data?.data
            if (responseData) {
                let failed = _.reduce(responseData, (result, value) => {
                    if (!value?.success ?? false) {
                        result.push(`Row ${value.row} - ${value.message}`)
                    }
                    return result
                }, [])

                if (failed.length > 0) {
                    alertDialog.addAlertDialog(t('dialog:upload_failed'), failed);
                    alertDialog.showAlert();
                } else {
                    alertDialog.addAlertDialog(
                        t('dialog:upload_success'),
                        t('dialog:how_many_incident_created', { count: responseData.length - failed.length })
                    );
                    alertDialog.showAlert();
                }
            }
            setUploadProgress((prev) => prev + progressOffset)
        }).catch(error => {
            alertDialog.addAlertDialog(t('dialog:upload_failed'), error.message || error.additional);
            alertDialog.showAlert();
        }).finally(() => {
            setUploadStarted(false);
            setUploadProgress(0);
            setUploadExcelDialog(false);
        })
    }

    const downloadExcel = async (params) => {
        try {
            setFullScreenLoading(true);
            let response = await downloadIncidentExcel(params);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Incidents ${moment.unix(params.from_date).format('YYYY-MM-DD')} - ${moment.unix(params.to_date).format('YYYY-MM-DD')}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            alertDialog.addAlertDialog(t('dialog:download_failed'), error.message || error.additional);
            alertDialog.showAlert();
            console.error(error);
        } finally {
            setFullScreenLoading(false);
        }
    }

    return (
        <Box className={classes.root}>
            <TopBar
                title={t('incident_management')}
                buttonClasses={classes.topBarButton}
                appBarClasses={classes.appBarClasses}
                handleRadioChange={handleManageIndex}
                radios={manageIndices}
                radio={manageIndex}
                buttons={
                    manageIndex === 'incident'
                        ? [
                            {
                                id: 1,
                                title: t('create_new'),
                                props: {
                                    startIcon: <AddBoxOutlined />,
                                    onClick: () => dispatch(handleOpenIncidentFormDialog({ open: true })),
                                },
                            },
                            {
                                id: 2,
                                title: t('common:upload'),
                                props: {
                                    startIcon: <Publish />,
                                    onClick: () => setUploadExcelDialog(true),
                                },
                            },
                            {
                                id: 3,
                                title: t('common:download'),
                                props: {
                                    startIcon: <CloudDownload />,
                                    onClick: () => downloadExcel(filters),
                                },
                            },
                        ]
                        : []
                }
            />
            <div hidden={manageIndex !== 'multiSearch'}>
                <IncidentMultiSearch />
            </div>
            <div hidden={manageIndex !== 'keyAccount'}>
                <IncidentKeyAccount />
            </div>
            <div hidden={manageIndex !== 'compensatedOrder'}>
                <IncidentCompensated />
            </div>
            {
                manageIndex === 'incident' ? (
                        <ParcelInvestigation
                            refreshDataRequest={isFormUpdated}
                            onDataRefresh={() => setIsFormUpdated(false)}
                            onFilterChange={(filters) => setFilters(filters)}
                        />
                ) : null
            }
            <EditIncidentFormDialog
                open={openIncidentForm.open}
                investigationId={openIncidentForm.id}
                incidentList={investigationList}
                onClose={(isUpdated) => {
                    dispatch(handleOpenIncidentFormDialog({ open: false }));
                    setIsFormUpdated(isUpdated);
                }}
                maxWidth="md"
                disableDefaultButtons
                fullWidth
            />
            <UniDialog
                title={t('common:upload')}
                open={uploadExcelDialog}
                onClose={() => setUploadExcelDialog(false)}
                onFinish={() => onFileSubmit(excelFile)}
                autoClose={false}
                maxWidth="xs"
                fullWidth
            >
                <UploadIncidentForm
                    onDataChange={(file) => setExcelFile(file)}
                    progress={uploadProgress}
                />
            </UniDialog>
            <FullscreenLoading open={fullScreenLoading || networkLoading} />
            <FullscreenLoading open={uploadStarted} progress={uploadProgress} />
        </Box>
    )
}

export default IncidentManagement;